import { find, path, pathOr } from 'ramda';
import {
  CUSTOMER_COMMISSION_PERCENTAGE,
  PRICING_VARIATION_WEEKDAYS_ID, PROVIDER_COMMISSION_PERCENTAGE
} from '../constants/pricing';
import {
  IListingAttributes,
  IMoney,
  IPricingVariation,
  ISearchParams,
} from '../utils/interface';
import { moment } from '../utils/date';
import { getTotalGuestsFromSearch } from '../utils/search';
import { getTimeStrAsObj } from '../utils/time';
import { isDynamicPrice } from '../utils/pricing';
import { Money } from '../utils/types';

interface IBookingParams {
  adultCount: number,
  bookingStart: object,
  bookingEnd: object,
  childCount: number,
  guestCount: number,
  totalHours: number,
};

export const formatBookingParams = (searchParams: ISearchParams): IBookingParams => {
  const { adults, bookingDate, children, endTime, startTime } = searchParams;
  const startingHour = startTime ? getTimeStrAsObj(startTime).hours : 0;
  const endingHour = endTime ? getTimeStrAsObj(endTime).hours : 0;
  const bookingStart = moment(bookingDate).hours(startingHour);
  const bookingEnd = moment(bookingDate).hours(endingHour);

  return {
    adultCount: adults || 0,
    bookingEnd,
    bookingStart,
    childCount: children || 0,
    guestCount: getTotalGuestsFromSearch(searchParams),
    totalHours: startTime && endTime ? moment(bookingEnd).diff(moment(bookingStart), 'hours') : 1,
  }
}

export const getPriceWithCustomerCommission = (price: IMoney, guestCount: Number) => {
  const customerCommissionValue = guestCount >= 10 ? 10 : CUSTOMER_COMMISSION_PERCENTAGE;

  return new Money(price.amount * (100 + customerCommissionValue) / 100, price.currency);
}

export const getWeekdayPricingVariation = (pricingVariationByPeriod: IPricingVariation[]) => {
  if (!pricingVariationByPeriod) return;

  return find((variation: { id: string }) => variation.id === PRICING_VARIATION_WEEKDAYS_ID, pricingVariationByPeriod);
};

export const getBasePricePerHourWithVariation = (listingAttributes: IListingAttributes, pricingVariation: IPricingVariation) => {
  const { price } = listingAttributes;
  const variationValue = pathOr(0, ['variation'], pricingVariation);
  const priceWithVariationAmount = variationValue ? price.amount * (100 - variationValue) / 100 : price.amount;

  return new Money(priceWithVariationAmount, price.currency);
};

export const getPricingVariationByPeriodValue = (listingAttributes: IListingAttributes, bookingDate: Object) => {
  const pricingVariationByPeriod = pathOr([], ['publicData', 'pricingVariationByPeriod'], listingAttributes);

  if (pricingVariationByPeriod.length === 0) return undefined;

  const bookingDay = moment(bookingDate).day();
  const isWeekdayBooking = bookingDay > 0 && bookingDay < 6;

  switch (true) {
    case isWeekdayBooking:
      const variation = find((variation: IPricingVariation) => variation.id === PRICING_VARIATION_WEEKDAYS_ID, pricingVariationByPeriod);

      return pathOr(0, ['variation'], variation);

    default:
      return undefined;
  }
};

export const getPricePerHour = (listingAttributes: IListingAttributes, bookingParams: IBookingParams): IMoney => {
  const { price } = listingAttributes;
  const { adultCount, bookingStart, childCount, guestCount } = bookingParams;
  const priceAmount = pathOr(0, ['amount'], price);
  const minAmountPerHour = pathOr(priceAmount, ['publicData', 'minAmountPerHour'], listingAttributes);
  const priceAmountPerHour = isDynamicPrice(minAmountPerHour, priceAmount, adultCount, childCount) ? minAmountPerHour / guestCount : priceAmount;
  const variationByPeriod = getPricingVariationByPeriodValue(listingAttributes, bookingStart);
  const priceWithVariationAmount = variationByPeriod ? priceAmountPerHour * (100 - variationByPeriod) / 100 : priceAmountPerHour;

  // @ts-ignore
  return new Money(priceWithVariationAmount, price.currency);
};

const getTotalPrice = (listingAttributes: IListingAttributes, bookingParams: IBookingParams, pricePerHour: IMoney): IMoney => {
  const { price } = listingAttributes;
  const minAmountPerHour = pathOr(price.amount, ['publicData', 'minAmountPerHour'], listingAttributes);
  const pricingExtraHours = path(['publicData', 'pricingExtraHours'], listingAttributes);
  const { adultCount, childCount, guestCount, totalHours } = bookingParams;

  const fromNbHours = pathOr(3, ['fromNbHours'], pricingExtraHours);
  const hasDiscountedHours = totalHours > fromNbHours;
  const childrenDiscountAmount = !isDynamicPrice(minAmountPerHour, price.amount, adultCount, childCount) && childCount ? (price.amount * childCount) * 0.5 * totalHours : 0;

  if (!pricingExtraHours || !hasDiscountedHours) {
    const totalPriceAmount = pricePerHour.amount * totalHours * guestCount;

    // @ts-ignore
    return new Money(totalPriceAmount - childrenDiscountAmount, price.currency);
  }

  const variation = pathOr(1, ['variation'], pricingExtraHours);
  const discountedHours = totalHours - fromNbHours;
  const discountPerExtraHour = price.amount * variation / 100;
  const totalPriceAmount = (pricePerHour.amount * totalHours - (discountPerExtraHour * discountedHours)) * guestCount;

  // @ts-ignore
  return new Money(totalPriceAmount - childrenDiscountAmount, price.currency);
};

export const getPrices = (listingAttributes: IListingAttributes, searchParams: ISearchParams) => {
  const { bookingDate, startTime, endTime, adults } = searchParams;
  const bookingParams = formatBookingParams(searchParams);
  const { guestCount, totalHours } = bookingParams;
  const pricePerHour = getPricePerHour(listingAttributes, bookingParams);

  switch (true) {
    case bookingDate && startTime && endTime && !!adults:
    case bookingDate && startTime && !!endTime:
    case bookingDate && !!adults:
      const totalPrice = getTotalPrice(listingAttributes, bookingParams, pricePerHour);
      const totalPriceWithFee = getPriceWithCustomerCommission(totalPrice, guestCount);

      return {
        // @ts-ignore
        mainPrice: new Money(totalPriceWithFee.amount / totalHours / guestCount, pricePerHour.currency),
        secondaryPrice: totalPriceWithFee,
      }

    case !!bookingDate:
      return {
        mainPrice: getPriceWithCustomerCommission(pricePerHour, guestCount),
      }

    default:
      return {}
  }
}


export const getMinTotalAmountPerBookingWithAllFees = (minTotalAmountPerBooking: string | undefined) => {
  try {
    if (!minTotalAmountPerBooking) return 0;

    const minTotalAmountPerBookingInt = parseInt(minTotalAmountPerBooking, 10);
    const getMoneyToCents = (amount: number) => amount * 100;
    return getMoneyToCents(minTotalAmountPerBookingInt / ((1 - (CUSTOMER_COMMISSION_PERCENTAGE / 100)) * (1 - (PROVIDER_COMMISSION_PERCENTAGE / 100)))).toFixed(2);
  } catch (e) {
    return 0;
  }
}
